import { type TextColors, cn, textColorMapping } from '@latitude/token-utils';
import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import type { PropsWithChildren, Ref } from 'react';

import { textVariants } from './variants/text-variants';

type TextProps = VariantProps<typeof textVariants> &
  PropsWithChildren & {
    /**
     * Whether the button is used as a child component within a Slot.
     * @default false
     */
    asChild?: boolean;
    /**
     * Custom class name to apply to the Text component.
     */
    className?: string;
    /**
     * The text color corresponding to Figma tokens.
     * ```
     * - `strong`   - Represents the token `color/ref/text/strong`       ` #FFFFFF`
     * - `disabled` - Represents the token `color/ref/text/disabled`     ` #4E5055`
     * - `muted`    - Represents the token `color/ref/text/muted`        ` #a2a4a9`
     * - `accent`   - Represents the token `color/ref/text/accent`       ` #03bfac`
     * - `inverted` - Represents the token `color/ref/text/inverted`     ` #000000`
     * - `default`  - Represents the token `color/ref/ui/status/default` ` #a2a4a9`
     * - `danger`   - Represents the token `color/ref/ui/status/danger`  ` #e8646f`
     * - `success`  - Represents the token `color/ref/ui/status/success` ` #79d297`
     * - `info`     - Represents the token `color/ref/ui/status/info`    ` #5580f6`
     * - `warning`  - Represents the token `color/ref/ui/status/warning` ` #ebb128`
     * - `alert`    - Represents the token `color/ref/ui/status/alert`   ` #ff7d52`
     * ```
     */
    color?: TextColors;
    'data-testid'?: string;
    title?: string;
    strikeThrough?: boolean;
  };

const Text = function Text({
  variant = 'bodyRegular',
  color = undefined,
  className = '',
  asChild = false,
  children = undefined,
  'data-testid': dataTestId = 'typography',
  title,
  strikeThrough = false,
  ref,
}: TextProps & {
  ref?: Ref<HTMLDivElement>;
}) {
  const Comp = asChild ? Slot : 'div';

  const textColor = (color && textColorMapping[color]) ?? '';
  return (
    <Comp
      ref={ref}
      title={title}
      data-testid={dataTestId}
      className={cn(
        textColor,
        textVariants({ variant }),
        { 'line-through': strikeThrough },
        className,
      )}
    >
      {children}
    </Comp>
  );
};

export { Text, type TextProps, textVariants };
